import { useState } from "react";
import { LayoutTemplate, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Agent, AgentTemplate } from "@/features/agents/api";
import { useUpdateAgent } from "@/features/agents/hooks";
import TemplatePickerCards from "@/features/agents/components/TemplatePickerCards";
import CreateTemplateDialog from "@/features/agents/components/CreateTemplateDialog";

type Props = {
  agent: Agent;
  canEdit: boolean;
};

export default function AgentTemplateTab({ agent, canEdit }: Props) {
  const update = useUpdateAgent(agent.id);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingTpl, setPendingTpl] = useState<AgentTemplate | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [agentType, setAgentType] = useState(agent.agent_type || "single_prompt");
  const [pipelineVersion, setPipelineVersion] = useState(
    agent.pipeline_version || "v3",
  );

  const typeChanged = agentType !== (agent.agent_type || "single_prompt");
  const pipelineChanged =
    pipelineVersion !== (agent.pipeline_version || "v3");
  const hasOtherChanges = typeChanged || pipelineChanged;

  async function saveOtherChanges() {
    try {
      await update.mutateAsync({
        agent_type: agentType,
        pipeline_version: pipelineVersion,
      });
      toast.success("Updated");
    } catch {
      toast.error("Failed to update");
    }
  }

  function onPick(tpl: AgentTemplate | null) {
    if (!tpl) {
      // "Start blank" from this dialog = clear the template link, leave
      // prompt/voice/language alone. Confirm so nothing is accidental.
      setPendingTpl(null);
      setPickerOpen(false);
      setConfirmOpen(true);
      return;
    }
    setPendingTpl(tpl);
    setPickerOpen(false);
    setConfirmOpen(true);
  }

  async function applyConfirmed() {
    setConfirmOpen(false);
    if (pendingTpl) {
      const tpl = pendingTpl;
      try {
        await update.mutateAsync({
          template: tpl.id,
          system_prompt: tpl.default_system_prompt,
          initial_message: tpl.default_initial_message,
          language: tpl.default_language || agent.language,
          voice_provider: tpl.default_voice_provider || agent.voice_provider,
          ...(tpl.default_voice_id ? { voice_id: tpl.default_voice_id } : {}),
        });
        toast.success(`Applied template: ${tpl.name}`);
      } catch {
        toast.error("Failed to apply template");
      }
    } else {
      try {
        await update.mutateAsync({ template: null });
        toast.success("Removed template link");
      } catch {
        toast.error("Failed to clear template");
      }
    }
    setPendingTpl(null);
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Template</CardTitle>
          <CardDescription>
            The vertical template this agent was created from. Switching the
            template overwrites the prompt and initial message — name and
            other settings stay.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-md border bg-background">
                {agent.template ? (
                  <LayoutTemplate className="size-5 text-primary" />
                ) : (
                  <Sparkles className="size-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <div className="text-sm font-medium">
                  {agent.template_name || "No template (created from scratch)"}
                </div>
                {agent.template_vertical && (
                  <div className="text-xs text-muted-foreground">
                    {agent.template_vertical}
                  </div>
                )}
              </div>
            </div>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                disabled={update.isPending}
              >
                <RefreshCw className="size-3.5" />
                {agent.template ? "Switch template" : "Apply template"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Architecture</CardTitle>
          <CardDescription>
            Pick how this agent is structured and which voice pipeline runs
            its calls.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Agent Type</Label>
            <Select
              value={agentType}
              onValueChange={setAgentType}
              disabled={!canEdit || update.isPending}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single_prompt">Single Prompt</SelectItem>
                <SelectItem value="flow_based">Flow Based</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Single Prompt: one system prompt drives the whole conversation
              (most common). Flow Based: uses a visual conversation flow.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Pipeline Version</Label>
            <Select
              value={pipelineVersion}
              onValueChange={setPipelineVersion}
              disabled={!canEdit || update.isPending}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="v1" disabled>
                  v1 — Legacy (retired)
                </SelectItem>
                <SelectItem value="v2" disabled>
                  v2 — Streaming (legacy)
                </SelectItem>
                <SelectItem value="v3">
                  v3 — Low-latency (recommended)
                </SelectItem>
                <SelectItem value="v4">
                  v4 — Flux turn-detection (eager, lowest-latency)
                </SelectItem>
                <SelectItem value="twilio">
                  Twilio — for Twilio phone numbers
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              v3 is the current low-latency default for new agents (Telnyx
              numbers). v4 adds Deepgram Flux turn-detection with eager
              speculative replies — fastest, but needs Flux enabled on your
              Deepgram plan. Choose Twilio for agents answering Twilio phone
              numbers. v1/v2 are legacy and can no longer be selected.
            </p>
          </div>

          {canEdit && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={saveOtherChanges}
                disabled={!hasOtherChanges || update.isPending}
              >
                {update.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Apply a template</DialogTitle>
            <DialogDescription>
              This will overwrite the agent's system prompt, initial message,
              language, and voice provider. Name and other settings stay.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <TemplatePickerCards
              onPick={onPick}
              selectedId={agent.template ?? null}
              blankLabel="Remove template link"
              blankHint="Keeps current prompt + voice; just clears the 'created from' link."
              onCreateNew={() => {
                setPickerOpen(false);
                setCreateOpen(true);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <CreateTemplateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(tpl) => {
          // After saving, immediately offer to apply it.
          setPendingTpl(tpl);
          setConfirmOpen(true);
        }}
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingTpl
                ? `Apply "${pendingTpl.name}" template?`
                : "Clear template link?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingTpl ? (
                <>
                  This will overwrite the System Prompt, Initial Message,
                  Language, and Voice Provider with the template's defaults.
                  Other fields (name, description, LLM, functions, KB) are
                  not changed.
                </>
              ) : (
                <>
                  This clears the "created from" link only. The current prompt
                  and voice settings stay untouched.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={update.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={update.isPending}
              onClick={(e) => {
                e.preventDefault();
                applyConfirmed();
              }}
            >
              {update.isPending
                ? "Applying…"
                : pendingTpl
                  ? "Apply"
                  : "Clear"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
